import React, { useEffect } from 'react';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { styled } from '@uhg-abyss/web/tools/styled';
import { useTIN } from '@src/state/taxInformation/hooks';
import { PageLayout } from '@src/common/PageLayout';
import { FLOW_ID } from '@src/common/AppConstants';
import { Link } from '@uhg-abyss/web/ui/Link';
import { config } from '@uhg-abyss/web/tools/config';
import { IconBrand } from '@uhg-abyss/web/ui/IconBrand';
import { IconSymbol } from '@uhg-abyss/web/ui/IconSymbol';
import { updateClaimInfo } from '@src/state/form/actions';
import { useFormState } from '@src/state/form/hooks';
import { useOnloading } from '@src/state/landing/index';
import { useUser } from '@src/state/user/index';
import { useEditing } from '@src/state/editing/hooks';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { event } from '@uhg-abyss/web/tools/event';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { tokenizer } from '@uhg-abyss/web/tools/tokenizer';
import { LagoonContext } from '@src/AppContext/LagoonContext';

const HeaderName = styled('h1', {
  margin: 'auto',
  fontSize: '36px',
  fontWeight: 600,
  paddingTop: '20px',
  textAlign: 'center',
  fontFamily: '$heading',
  color: '$primary1',
});

const SubheaderName = styled('h1', {
  fontSize: '26px',
  fontWeight: 600,
  paddingTop: '20px',
  textAlign: 'center',
  fontFamily: '$heading',
  color: '$primary1',
});

const TopInstruction = styled('p', {
  fontSize: '16px',
  fontWeight: 500,
  margin: '32px auto 0 auto',
  textAlign: 'center',
  fontFamily: 'AbyssEnterpriseSansVF, Arial, sans-serif',
  maxWidth: '1100px',
});

const LayoutInstruction = styled('p', {
  fontWeight: 500,
  paddingTop: '10px',
  fontFamily: '$primary',
});

const LayoutNeedText = styled('text', {
  fontWeight: 'bold',
  fontFamily: '$primary',
});

const Layout = styled('div', {
  flex: '1 1 0',
  minWidth: '330px',
  maxWidth: '370px',
  background: '$white',
  border: '1px solid rgb(219, 219, 219)',
  borderRadius: '3px',
  boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
  paddingLeft: 20,
  display: 'flex',
  flexDirection: 'column',
  margin: '10px',
});

const LayoutHeader = styled('h2', {
  fontSize: '22px',
  fontWeight: 600,
  fontFamily: '$heading',
  color: '$primary1',
  width: '80%',
});

const HorizontalView = styled('div', {
  margin: 'auto',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'stretch',
  width: '100%',
  marginTop: 10,
});

const LayoutButton = styled('div', {
  width: '280px',
  padding: '10px',
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-around',
  borderWidth: '2.5px',
  borderColor: '$primary1',
  borderStyle: 'solid',
  borderRadius: '30px',
  marginTop: 'auto',
  marginBottom: '25px',
  alignItems: 'center',
  '&:hover': {
    cursor: 'pointer',
  },
});

const ButtonText = styled('text', {
  color: '$primary1',
  fontWeight: 'bold',
  marginLeft: '10px',
  fontSize: '16px',
});

export const Landing = (props) => {
  const lagoonContext = React.useContext(LagoonContext);
  const featureToggles = lagoonContext.getTable('feature-toggles');
  const uiMessages = lagoonContext.getTable('ui-messaging');
  const { setCurrentFlow } = useOnloading.actions();
  const { saveUserInfo, getFetchUser } = useUser.actions();
  const { resetTINInfo } = useTIN.actions();
  const { setIsEditing } = useEditing.actions();
  const router = useRouter();
  const { token } = useParams();
  const parsedToken = tokenizer.parse(token);
  const isAPIMarketPlaceUser = parsedToken?.sourceSystem === 'apim';

  const { actions } = useFormState({ updateClaimInfo });

  const navigateToTinValidator = (sourceTile) => {
    setCurrentFlow(sourceTile);
    router.navigate(`/TINValidation/${sourceTile}/${token}`);
  };

  const formatPhoneNumber = (phone) => {
    return phone ? phone.replace(/[^0-9]/g, '') : '';
  };

  const clearClaimInformation = () => {
    actions.updateClaimInfo({
      memberId: '',
      patientDob: '',
      claimNumber: '',
      fDOS: '',
      lDOS: '',
      procedureCode: '',
      revenueCode: '',
    });
  };

  useEffect(() => {
    setIsEditing(false);
    getFetchUser(
      {
        url:
          uiMessages.getRow('OIDC_API_URL')?.message || config('OIDC_API_URL'),
      },
      (userResponse) => {
        if (userResponse.error) {
          if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
            event('API_TRANSACTION_ERROR', {
              errorMessage: userResponse.error.data?.error?.description,
              errorApiCode: userResponse.error.data?.error?.code,
              errorHttpsCode: userResponse.error.status,
            });
          }
          router.navigate(`/error-page/${token}`);
        }
        const { firstName, lastName, uuid, email, phoneExt, username } =
          userResponse;
        const phone = formatPhoneNumber(userResponse.phone);
        const data = {
          userId: username,
          userUUID: uuid,
          firstName,
          lastName,
          phone,
          phoneExt,
          email,
          isAPIMarketPlaceUser,
          url:
            uiMessages.getRow('OIDC_API_URL')?.message ||
            config('OIDC_API_URL'),
        };
        const dataforChat = {
          username,
          uuid,
          userFirstName: firstName,
          userLastName: lastName,
          phone,
          phoneExt,
          userEmail: email,
          isAPIMarketPlaceUser,
        };
        resetTINInfo();
        saveUserInfo(data);
        if (!isAPIMarketPlaceUser) {
          event('REGISTRATION_HOME_PAGE_LOAD_LIVEPERSON', { ...dataforChat });
        } else {
          clearClaimInformation();
        }

        if (featureToggles.getRow('ADOBE_ANALYTICS_TRACKING')?.active) {
          const pageName = 'digitalsecurity:home';

          event('ON_PAGE_LOAD', {
            pageName,
            orgType: '',
            organization: '',
            uuid,
            tin: '',
            section1: 'home',
            section2: '',
            section3: '',
          });
        }
      }
    );
  }, []);

  const navToRegistrationGuide = () => {
    if (isAPIMarketPlaceUser) {
      return config('UHC_REGISTRATION_GUIDE_APIM');
    }
    return config('UHC_REGISTRATION_GUIDE_PROVIDER');
  };

  return (
    <PageLayout>
      <Grid.Col>
        <HeaderName>
          {isAPIMarketPlaceUser
            ? 'Welcome to API Marketplace!'
            : 'Welcome to the Provider Portal!'}
        </HeaderName>
        <TopInstruction>
          To register and set
          {isAPIMarketPlaceUser ? ' API access ' : ' permissions '}
          for you and your office personnel, complete these steps that will
          provide us information about your business, including your federal tax
          ID. Visit our{' '}
          <Link
            css={{
              'abyss-link-root': {
                fontSize: '16px',
              },
            }}
            href={navToRegistrationGuide()}
            hideIcon
            onClick={() => {
              event('DOWNLOAD_CLICKS', {
                downloadFileName: 'registration guide',
                fileUrl: navToRegistrationGuide(),
                fileContext: 'self paced chameleon guide',
              });
            }}
          >
            registration guide
          </Link>{' '}
          to learn more about the process.
        </TopInstruction>
      </Grid.Col>
      <Grid.Col>
        <SubheaderName>
          First, select your organization type below.
        </SubheaderName>
      </Grid.Col>

      <Grid.Col>
        <HorizontalView>
          <Layout>
            <IconBrand
              icon="stethoscope"
              size={65}
              style={{
                marginTop: '25px',
                marginBottom: '25px',
              }}
              variant="twotone"
            />
            <LayoutHeader>Healthcare Professional or Facility</LayoutHeader>
            <LayoutInstruction>
              Physician, Hospital/Clinic (including provider billing Dept.), or
              other healthcare provider.
            </LayoutInstruction>
            <LayoutInstruction>
              <LayoutNeedText>You'll need: </LayoutNeedText>
              your healthcare organization's tax ID number (TIN).
            </LayoutInstruction>
            <LayoutButton
              onClick={() => {
                return navigateToTinValidator(
                  isAPIMarketPlaceUser
                    ? FLOW_ID.API_MARKETPLACE_PROVIDER
                    : FLOW_ID.PROVIDER
                );
              }}
            >
              <ButtonText>Register with my Practice</ButtonText>
              <IconSymbol icon="arrow_forward" size={20} color="$primary1" />
            </LayoutButton>
          </Layout>
          <Layout>
            <IconBrand
              icon="cost"
              variant="twotone"
              size={65}
              style={{
                marginTop: '25px',
                marginBottom: '25px',
              }}
            />
            <LayoutHeader>
              Revenue Cycle Management/ Billing Company
            </LayoutHeader>
            <LayoutInstruction>
              Billing Agent, Managed Billing, Medical Billing Services, Revenue
              Cycle Management submitting claims, collecting payments for a
              practice, hospital or other medical groups.
            </LayoutInstruction>
            <LayoutInstruction>
              <LayoutNeedText>You'll need: </LayoutNeedText>
              your billing company's tax ID number (TIN).
            </LayoutInstruction>
            <LayoutButton
              onClick={() => {
                return navigateToTinValidator(
                  isAPIMarketPlaceUser
                    ? FLOW_ID.API_MARKETPLACE_BILLING
                    : FLOW_ID.BILLING
                );
              }}
            >
              <ButtonText>Register with my Company</ButtonText>
              <IconSymbol icon="arrow_forward" size={20} color="$primary1" />
            </LayoutButton>
          </Layout>
          <Layout>
            <IconBrand
              icon="bank_card"
              variant="twotone"
              size={65}
              style={{
                marginTop: '25px',
                marginBottom: '25px',
              }}
            />
            <LayoutHeader>Business Vendor</LayoutHeader>
            <LayoutInstruction>
              Clearinghouse, Practice Management Systems (PMS), or other vendor
              service.
            </LayoutInstruction>
            <LayoutInstruction>
              This option does not include Billing Services; please select the
              Billing registration option.
            </LayoutInstruction>
            <LayoutInstruction>
              <LayoutNeedText>You'll need: </LayoutNeedText>
              your company's tax ID number (TIN).
            </LayoutInstruction>
            <LayoutButton
              style={{ width: '235px' }}
              onClick={() => {
                return navigateToTinValidator(
                  isAPIMarketPlaceUser
                    ? FLOW_ID.API_MARKETPLACE_VENDOR
                    : FLOW_ID.VENDOR
                );
              }}
            >
              <ButtonText>Register as a Vendor</ButtonText>
              <IconSymbol icon="arrow_forward" size={20} color="$primary1" />
            </LayoutButton>
          </Layout>
        </HorizontalView>
      </Grid.Col>
    </PageLayout>
  );
};
